import { useEffect, useMemo } from 'react';
import { isValidUUID } from '@/lib/uuid-validator';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

type InvoiceRow = {
  uuid?: string | null;
  id_hd?: string | null;
};

type InvoiceLookup = Record<string, unknown> & {
  ID?: string;
  document_uuid?: string;
};

interface UseInvoiceHydrationOptions<T extends InvoiceRow> {
  rows: T[];
  enabled?: boolean;
  referenceField?: string;
  dataField?: string;
  onHydrated: (rowUuid: string, invoice: InvoiceLookup) => void;
}

export function useInvoiceHydration<T extends InvoiceRow>({
  rows,
  enabled = true,
  referenceField = 'id_hd',
  dataField = 'id_hd_data',
  onHydrated
}: UseInvoiceHydrationOptions<T>) {
  const { entity } = useAuth();
  const pendingRows = useMemo(
    () =>
      rows.filter(row => {
        const keyedRow = row as InvoiceRow & Record<string, unknown>;
        const invoice = keyedRow[dataField] as Record<string, unknown> | null | undefined;
        const referenceUuid = isValidUUID(keyedRow[referenceField]);
        return Boolean(row.uuid && referenceUuid && !invoice?.document_uuid && !invoice?.ID);
      }),
    [dataField, referenceField, rows]
  );
  const requestKey = pendingRows
    .map(row => `${row.uuid}:${isValidUUID((row as InvoiceRow & Record<string, unknown>)[referenceField]) || ''}`)
    .join('|');

  useEffect(() => {
    if (!enabled || !entity?.slug || pendingRows.length === 0) return;

    let active = true;
    const documentUuids = [
      ...new Set(
        pendingRows
          .map(row => (row as InvoiceRow & Record<string, unknown>)[referenceField])
          .map(isValidUUID)
          .filter((uuid): uuid is string => Boolean(uuid))
      )
    ];

    void api
      .post<
        InvoiceLookup[]
      >(`/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_THEO_KHACH_HANG_BY_UUIDS}/`, { document_uuids: documentUuids })
      .then(response => {
        if (!active) return;
        const invoices = new Map(
          response.data
            .map(invoice => [isValidUUID(invoice.document_uuid) || isValidUUID(invoice.ID), invoice] as const)
            .filter((entry): entry is readonly [string, InvoiceLookup] => Boolean(entry[0]))
        );
        pendingRows.forEach(row => {
          const referenceUuid = isValidUUID((row as InvoiceRow & Record<string, unknown>)[referenceField]);
          const invoice = referenceUuid ? invoices.get(referenceUuid) : undefined;
          if (row.uuid && invoice) onHydrated(row.uuid, invoice);
        });
      });

    return () => {
      active = false;
    };
  }, [enabled, entity?.slug, onHydrated, pendingRows, referenceField, requestKey]);
}
